/**
 * Resolved map-layer token values (concrete hex/numbers) for MapLibre. The
 * renderer draws to a canvas, so it can't use the CSS custom properties the
 * rest of the UI consumes — it needs literal values. Those literals live only
 * in the compiled token artifact (`design/tokens/dist/<theme>/theme.json`,
 * the sanctioned home per DESIGN_SYSTEM.md), and this module reads them from
 * there. No map color is ever hardcoded in renderer/UI source.
 *
 * The cartographic palette, ramps, and tile colors are FIXED across brands
 * (§12: only the accent family + marker fill are brand-mutable); this accessor
 * reads them per-theme anyway so `marker.fill` follows the active accent.
 */

import plotzioTheme from "../../../design/tokens/dist/plotzio/theme.json";
import meridianTheme from "../../../design/tokens/dist/meridian-example/theme.json";

const THEMES: Record<string, { tokens: Record<string, unknown> }> = {
  [plotzioTheme.name]: plotzioTheme as { tokens: Record<string, unknown> },
  [meridianTheme.name]: meridianTheme as { tokens: Record<string, unknown> },
};

export const DEFAULT_MAP_THEME = "plotzio";

export interface MapTokens {
  /** Categorical qualitative palette, fixed order (map.palette.categorical.1..8). */
  palette: string[];
  /** The "Other" bucket color for categories beyond the 12-cap. */
  paletteOther: string;
  /** Sequential single-hue ramp, light→dark (7 steps). */
  sequential: string[];
  /** Heat density ramp. */
  heat: string[];
  /** Diverging ramp. */
  diverging: string[];
  tile: {
    land: string;
    water: string;
    greenspace: string;
    /** Vegetated landcover wash (wood/grass) — lighter than greenspace parks. */
    landcover: string;
    roadFill: string;
    roadCasing: string;
    /** Motorway ribbon — the one warm road accent. */
    motorway: string;
    boundary: string;
    contour: string;
    building: string;
  };
  darkField: string;
  marker: {
    fill: string;
    ring: string;
    ringWidth: number;
    size: number;
    sizeDense: number;
    sizeEmphasized: number;
    /** Teardrop-pin (FAIL-476) colors — the SDF body tint + the white head dot/halo.
     *  (The darker `pin.rim` design token stays in tokens.json for the CSS
     *  `--map-marker-pin-rim` consumer; the SDF pin's ring is the white halo, not a
     *  separate rim color, so it isn't loaded into the runtime tokens.) */
    pin: { fill: string; dot: string };
    /** Hollow-variant fill for low-confidence geocodes (paper). */
    lowConfidenceFill: string;
    lowConfidenceStrokeWidth: number;
    /** Filtered-out ghost opacity. */
    dimmedOpacity: number;
    /** Selected/highlight ring (ink) + halo (accent subtle). */
    selectedRing: string;
    selectedHalo: string;
    selectedHaloSize: number;
  };
  cluster: { fill: string; ring: string; ringWidth: number; text: string; sizes: number[] };
  accuracy: { high: string; medium: string; low: string };
  label: {
    ink: string;
    /** Secondary place labels (neighbourhoods). */
    secondary: string;
    /** Text halo — matches the land paper so labels sit IN the map. */
    halo: string;
    family: string;
    waterFamily: string;
    waterStyle: string;
  };
}

function str(tokens: Record<string, unknown>, path: string): string {
  const v = tokens[path];
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  throw new Error(`map token "${path}" missing or not a string`);
}

function num(tokens: Record<string, unknown>, path: string): number {
  const v = tokens[path];
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) throw new Error(`map token "${path}" is not numeric`);
  return n;
}

function arr(tokens: Record<string, unknown>, path: string): string[] {
  const v = tokens[path];
  if (Array.isArray(v)) return v.map(String);
  throw new Error(`map token "${path}" is not an array`);
}

/**
 * First declared face of the label family token — the glyph fontstack name a
 * symbol layer requests (`{fontstack}` in the glyphs URL template). Shared by
 * the renderer (text-font) and the server bootstrap (glyph availability probe).
 */
export function labelFontstack(tokens: MapTokens): string {
  return tokens.label.family.split(",")[0]!.trim().replace(/^['"]|['"]$/g, "");
}

export function getMapTokens(themeName: string = DEFAULT_MAP_THEME): MapTokens {
  const theme = THEMES[themeName] ?? THEMES[DEFAULT_MAP_THEME]!;
  const t = theme.tokens;
  return {
    palette: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => str(t, `map.palette.categorical.${i}`)),
    paletteOther: str(t, "map.palette.categorical.other"),
    sequential: arr(t, "map.ramp.sequential"),
    heat: arr(t, "map.ramp.heat"),
    diverging: arr(t, "map.ramp.diverging"),
    tile: {
      land: str(t, "map.tile.land"),
      water: str(t, "map.tile.water"),
      greenspace: str(t, "map.tile.greenspace"),
      landcover: str(t, "map.tile.landcover"),
      roadFill: str(t, "map.tile.roadFill"),
      roadCasing: str(t, "map.tile.roadCasing"),
      motorway: str(t, "map.tile.motorway"),
      boundary: str(t, "map.tile.boundary"),
      contour: str(t, "map.tile.contour"),
      building: str(t, "map.tile.building"),
    },
    darkField: str(t, "map.tile.dark.field"),
    marker: {
      fill: str(t, "map.marker.fill.default"),
      ring: str(t, "map.marker.ring.color"),
      ringWidth: num(t, "map.marker.ring.width"),
      size: num(t, "map.marker.size.default"),
      sizeDense: num(t, "map.marker.size.dense"),
      sizeEmphasized: num(t, "map.marker.size.emphasized"),
      pin: {
        fill: str(t, "map.marker.pin.fill"),
        dot: str(t, "map.marker.pin.dot"),
      },
      lowConfidenceFill: str(t, "map.marker.lowConfidence.fill"),
      lowConfidenceStrokeWidth: num(t, "map.marker.lowConfidence.strokeWidth"),
      dimmedOpacity: num(t, "map.marker.dimmedOpacity"),
      selectedRing: str(t, "map.marker.selected.ringColor"),
      selectedHalo: str(t, "map.marker.selected.haloColor"),
      selectedHaloSize: num(t, "map.marker.selected.haloSize"),
    },
    cluster: {
      fill: str(t, "map.cluster.fill"),
      ring: str(t, "map.cluster.ringColor"),
      ringWidth: num(t, "map.cluster.ringWidth"),
      text: str(t, "map.cluster.text"),
      sizes: [1, 2, 3, 4].map((i) => num(t, `map.cluster.size.${i}`)),
    },
    accuracy: {
      high: str(t, "map.accuracy.high"),
      medium: str(t, "map.accuracy.medium"),
      low: str(t, "map.accuracy.low"),
    },
    label: {
      ink: str(t, "map.label.ink"),
      secondary: str(t, "map.label.secondary"),
      halo: str(t, "map.label.halo"),
      family: str(t, "map.label.family"),
      waterFamily: str(t, "map.label.water.family"),
      waterStyle: str(t, "map.label.water.style"),
    },
  };
}
